import { memo, useEffect, useState, type ReactElement } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Select,
  Typography,
} from '@mui/material';
import {
  AWS_FOUR_USE, AWS_THREE_USE, AWS_USE_SHIPPED, FILTERED_LISTINGS, JIT_SHIPPED, OUTSIDE_ACCESS_CHECK,
} from '../catalog';
import { useSetToolAccess, useToolAccess } from '../hooks/useToolConfigs';
import { needsToolWideLowering, openings, roleNeeded } from '../utils/accessChange';
import type { ToolAccess, ToolAccessSettings } from '../../../types/backend';
import StoredReadFailed from './StoredReadFailed';

type Key = keyof ToolAccess;
interface Option { value: string; label: string }

const LEVEL_NAME: Record<string, string> = { l1: 'L1', l2: 'L2', admin: 'Admin' };
// The shipped level of each use, as the policies give it (tm-design E2), named as the options name it.
const USE_SHIPPED: Record<string, string> = Object.fromEntries(Object.entries(AWS_USE_SHIPPED).map(([use, level]) => {
  const name = LEVEL_NAME[level];
  if (!name) throw new Error(`Unknown shipped level ${level} for ${use}: regenerate the tool catalog`);
  return [use, name];
}));
const FOUR_USES: { key: Key; label: string }[] = [
  { key: 'access_aws_readonly', label: 'Read-only' },
  { key: 'access_aws_write', label: 'Write on an approved service' },
  { key: 'access_aws_write_other', label: 'Write on any other service' },
  { key: 'access_aws_automation', label: 'Automation role' },
];
// Three uses: one write row sets both write keys, which the save keeps equal.
const THREE_USES: { key: Key; label: string }[] = [
  { key: 'access_aws_readonly', label: 'Read-only' },
  { key: 'access_aws_write', label: 'Write' },
  { key: 'access_aws_automation', label: 'Automation role' },
];
const KEY_LABEL: Record<string, string> = {
  access_required: 'Level', access_admin_role: 'Admin role', access_jit: 'Just-in-time',
  access_batch: 'Automation agents',
  ...Object.fromEntries(FOUR_USES.map((u) => [u.key, u.label])),
};
// "unset" is the before of a row the automation seed wrote where nothing was stored.
const VALUE_LABEL: Record<string, string> = {
  shipped: 'as shipped', l1: 'L1', l2: 'L2', admin: 'admin', required: 'required', off: 'off', unreadable: 'unreadable',
  unset: 'not set',
};

const levelOptions = (shipped: string, values: string[]): Option[] =>
  values.map((v) => ({ value: v, label: v === 'shipped' ? `As shipped (${shipped})` : LEVEL_NAME[v] }));

interface ToolAccessPanelProps {
  actionId: string;
  editable: boolean;
  /** The policy set gives this action the admin role by default (tool-access-floor's shipped branch). */
  roleGated: boolean;
  /** The operator level the action needs as shipped, for the "As shipped (L2)" label. */
  shippedLevel: string;
}

// What a save sends: the seven settings. The automation tick is the kind mark's to write.
const settingsOf = (a: ToolAccess): ToolAccessSettings => ({
  access_required: a.access_required, access_admin_role: a.access_admin_role, access_jit: a.access_jit,
  access_aws_readonly: a.access_aws_readonly, access_aws_write: a.access_aws_write,
  access_aws_write_other: a.access_aws_write_other, access_aws_automation: a.access_aws_automation,
});

/** Who may use an action: its level, the admin role, just-in-time and, set per use, the AWS role levels. */
const ToolAccessPanel = memo<ToolAccessPanelProps>(({ actionId, editable, roleGated, shippedLevel }) => {
  const outside = OUTSIDE_ACCESS_CHECK.has(actionId);
  const { data: view, isError: readFailed } = useToolAccess(actionId, !outside);
  const setAccess = useSetToolAccess();
  const [draft, setDraft] = useState<ToolAccess | null>(null);
  const [confirming, setConfirming] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(view ? { ...view.access } : null);
    setError(null);
  }, [view]);

  if (outside) {
    return (
      <Typography variant="body2" sx={{ mb: 1 }}>
        Open to every signed-in caller: outside the access check. It answers only about the caller.
      </Typography>
    );
  }
  if (readFailed) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Who may use it</Typography>
        <StoredReadFailed />
      </Box>
    );
  }
  if (!view || !draft) return null;

  const uses = AWS_FOUR_USE.has(actionId) ? FOUR_USES : AWS_THREE_USE.has(actionId) ? THREE_USES : [];
  const threeUse = AWS_THREE_USE.has(actionId);
  // A tool-wide lowering never reaches the four-use tools (policy 2 excludes them); the three-use ones keep it.
  const raiseOnly = AWS_FOUR_USE.has(actionId);
  const canEdit = editable && !view.locked_reason;
  const set = (key: Key, value: string) => {
    const next = { ...draft, [key]: value } as ToolAccess;
    if (threeUse && key === 'access_aws_write') next.access_aws_write_other = value as ToolAccess['access_aws_write_other'];
    setDraft(next);
  };
  const toggleRole = (checked: boolean) =>
    set('access_admin_role', checked ? (roleGated ? 'shipped' : 'required') : (roleGated ? 'off' : 'shipped'));

  const commit = async () => {
    setConfirming(null);
    try {
      await setAccess.mutateAsync({ action_id: actionId, access: settingsOf(draft) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };
  const saveClicked = () => {
    const opens = openings(view.access, draft, roleGated);
    if (opens.length) setConfirming(opens);
    else void commit();
  };

  const select = (testId: string, key: Key, options: Option[]) => (
    <Select data-testid={testId} size="small" value={draft[key]} disabled={!canEdit}
      onChange={(e) => set(key, e.target.value as string)} sx={{ minWidth: 170 }}>
      {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </Select>
  );
  const row = (label: string, control: ReactElement) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Typography variant="body2" sx={{ width: 190 }}>{label}</Typography>
      {control}
    </Box>
  );
  const jitShipped = JIT_SHIPPED.has(actionId) ? 'on' : 'off';
  // "as shipped" in the history names the level it stands for.
  const shippedOf: Record<string, string> = {
    ...USE_SHIPPED, access_required: shippedLevel, access_jit: jitShipped,
    access_admin_role: roleGated ? 'required' : 'not required',
  };
  const historyValue = (key: string, value: string) =>
    value === 'shipped' ? `as shipped (${shippedOf[key]})` : VALUE_LABEL[value] ?? value;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Who may use it</Typography>
      {view.locked_reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{view.locked_reason}</Typography>}
      {view.malformed && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          The stored setting is unreadable, so every operator is refused it. Save to repair it.
        </Typography>
      )}
      {row(raiseOnly ? 'Level (raise only)' : 'Level', select('access-level', 'access_required',
        levelOptions(shippedLevel, raiseOnly ? ['shipped', 'l2', 'admin'] : ['shipped', 'l1', 'l2', 'admin'])))}
      {uses.map((use) => (
        <Box key={use.key}>{row(use.label, select(`access-use-${use.key}`, use.key,
          levelOptions(USE_SHIPPED[use.key], ['shipped', 'l1', 'l2', 'admin'])))}</Box>
      ))}
      {row('Just-in-time', select('access-jit', 'access_jit', [
        { value: 'shipped', label: `As shipped (${jitShipped})` }, { value: 'required', label: 'On' },
        { value: 'off', label: 'Off' },
      ]))}
      <FormControlLabel
        label="Needs admin role"
        control={(
          <Checkbox size="small" checked={roleNeeded(draft, roleGated)}
            disabled={!canEdit || Boolean(view.admin_role_lock)} onChange={(e) => toggleRole(e.target.checked)} />
        )}
      />
      {view.admin_role_lock && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{view.admin_role_lock}</Typography>
      )}
      {needsToolWideLowering(actionId) && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
          Lowering its write use alone still refuses L1: it also needs its tool-wide level lowered.
        </Typography>
      )}
      {FILTERED_LISTINGS.has(actionId) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          A list: a caller below a raised level gets an empty list, not a refusal.
        </Typography>
      )}
      {canEdit && (
        <Button size="small" variant="contained" sx={{ mt: 1 }} disabled={setAccess.isPending} onClick={saveClicked}>
          Save access
        </Button>
      )}
      {error && <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{error}</Typography>}

      {view.history.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Changes</Typography>
          {/* A three-use action's one write use is two keys saved together: one row. */}
          {view.history.filter((c) => !(threeUse && c.key === 'access_aws_write_other')).map((c) => (
            <Typography key={`${c.at}-${c.key}`} variant="caption" sx={{ display: 'block' }}>
              {new Date(c.at).toLocaleString()} · {c.actor_email ?? c.actor_id} ·{' '}
              {threeUse && c.key === 'access_aws_write' ? 'Write' : KEY_LABEL[c.key] ?? c.key}:{' '}
              {historyValue(c.key, c.before)} → {historyValue(c.key, c.after)}
            </Typography>
          ))}
        </Box>
      )}

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogTitle>This opens what only the admin role has today</DialogTitle>
        <DialogContent>
          {confirming?.map((line) => <Typography key={line} variant="body2">{line}</Typography>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={commit}>Open it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ToolAccessPanel.displayName = 'ToolAccessPanel';

export default ToolAccessPanel;
